/**
 * Crosova shared types
 * Used by API, web app, and mobile app.
 */

export enum Platform {
  EBAY = 'ebay',
  VINTED = 'vinted',
  DEPOP = 'depop',
  ETSY = 'etsy',
  FACEBOOK = 'facebook',
  GRAILED = 'grailed',
  MERCARI = 'mercari',
  POSHMARK = 'poshmark',
  SHOPIFY = 'shopify',
  WHATNOT = 'whatnot',
  BIGCOMMERCE = 'bigcommerce',
  WOOCOMMERCE = 'woocommerce',
}

export const PLATFORM_NAMES: Record<Platform, string> = {
  [Platform.EBAY]: 'eBay',
  [Platform.VINTED]: 'Vinted',
  [Platform.DEPOP]: 'Depop',
  [Platform.ETSY]: 'Etsy',
  [Platform.FACEBOOK]: 'Facebook Marketplace',
  [Platform.GRAILED]: 'Grailed',
  [Platform.MERCARI]: 'Mercari',
  [Platform.POSHMARK]: 'Poshmark',
  [Platform.SHOPIFY]: 'Shopify',
  [Platform.WHATNOT]: 'Whatnot',
  [Platform.BIGCOMMERCE]: 'BigCommerce',
  [Platform.WOOCOMMERCE]: 'WooCommerce',
};

export enum ListingStatus {
  DRAFT = 'draft',
  PUBLISHING = 'publishing',
  LIVE = 'live',
  SOLD = 'sold',
  FAILED = 'failed',
  DELISTED = 'delisted',
}

export enum Condition {
  NEW_WITH_TAGS = 'new_with_tags',
  NEW_WITHOUT_TAGS = 'new_without_tags',
  EXCELLENT = 'excellent',
  GOOD = 'good',
  FAIR = 'fair',
}

export const CONDITION_LABELS: Record<Condition, string> = {
  [Condition.NEW_WITH_TAGS]: 'New with tags',
  [Condition.NEW_WITHOUT_TAGS]: 'New without tags',
  [Condition.EXCELLENT]: 'Excellent used',
  [Condition.GOOD]: 'Good used',
  [Condition.FAIR]: 'Fair',
};

export enum SubscriptionPlan {
  TRIAL = 'trial',
  STARTER = 'starter',
  PRO = 'pro',
  PAY_ON_SALE = 'pay_on_sale',
}

export enum JobType {
  CROSSLIST = 'crosslist',
  DELIST = 'delist',
  IMPORT = 'import',
  SYNC = 'sync',
  PRICE_UPDATE = 'price_update',
}

export enum JobStatus {
  QUEUED = 'queued',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  RETRYING = 'retrying',
}

// ─────────────────────────────────────────────────────────
// Core entities
// ─────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  trialEndsAt?: string;
  plan: SubscriptionPlan;
  stripeCustomerId?: string;
  onboardingCompleted: boolean;
}

export interface PlatformAccount {
  id: string;
  userId: string;
  platform: Platform;
  connected: boolean;
  username?: string;
  oauthExpiresAt?: string;
  lastSyncedAt?: string;
}

export interface Listing {
  id: string;
  userId: string;
  title: string;
  description: string;
  price: number;        // in pence (GBP)
  currency: string;
  condition: Condition;
  brand?: string;
  colour?: string;
  size?: string;
  category?: string;
  subcategory?: string;
  sku?: string;
  tags: string[];
  folder?: string;
  status: ListingStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ListingPhoto {
  id: string;
  listingId: string;
  url: string;
  position: number;
  isCover: boolean;
}

export interface PlatformListing {
  id: string;
  listingId: string;
  platform: Platform;
  externalId?: string;
  externalUrl?: string;
  status: ListingStatus;
  publishedAt?: string;
  errorMessage?: string;
  retryCount: number;
}

export interface Sale {
  id: string;
  listingId: string;
  platform: Platform;
  salePrice: number;
  fees: number;
  netRevenue: number;
  buyer?: string;
  saleDate: string;
}

export interface ListingJob {
  id: string;
  userId: string;
  listingId?: string;
  type: JobType;
  status: JobStatus;
  progress: number;       // 0-100
  totalSteps: number;
  completedSteps: number;
  error?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface AuditLog {
  id: string;
  userId: string;
  entityType: 'listing' | 'platform_account' | 'sale' | 'subscription';
  entityId: string;
  action: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}
